// src/utils/time.js
// Regional date/time display helpers for picker ↔ server conversion.
// IMPORTANT: Treat picker values and server strings as wall time without timezone math.

const pad = (n) => String(n).padStart(2, "0");

/**
 * Convert <input type="datetime-local"> value (e.g., "2025-08-20T10:15")
 * to an academy wall-time string that the backend (LocalDateTime) accepts:
 * "YYYY-MM-DDTHH:mm:ss"
 *
 * No timezone math — the digits the user picked are the intended wall time.
 */
export function inputLocalToWallTimeString(inputValue) {
  if (!inputValue) return "";
  // Handles "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(inputValue)) {
    return `${inputValue}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(inputValue)) {
    return inputValue;
  }
  // Best effort: try to construct from Date without altering the wall-time digits
  try {
    const d = new Date(inputValue);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const HH = pad(d.getHours());
    const MI = pad(d.getMinutes());
    return `${yyyy}-${MM}-${DD}T${HH}:${MI}:00`;
  } catch {
    return "";
  }
}

/** @deprecated Use inputLocalToWallTimeString */
export const inputLocalToISTString = inputLocalToWallTimeString;

/**
 * Convert server wall-time (LocalDateTime string)
 *   "YYYY-MM-DDTHH:mm[:ss]"  -> value for <input type="datetime-local">: "YYYY-MM-DDTHH:mm"
 */
export function wallTimeServerToInputValue(wallTimeString) {
  if (!wallTimeString) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(wallTimeString)) {
    return `${wallTimeString}T00:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(wallTimeString)) {
    return wallTimeString;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(wallTimeString)) {
    return wallTimeString.slice(0, 16);
  }
  // Fallback: try parsing; we still only return the numeric wall time
  try {
    const d = new Date(wallTimeString);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const HH = pad(d.getHours());
    const MI = pad(d.getMinutes());
    return `${yyyy}-${MM}-${DD}T${HH}:${MI}`;
  } catch {
    return "";
  }
}

/** @deprecated Use wallTimeServerToInputValue */
export const istServerToInputValue = wallTimeServerToInputValue;

/**
 * Add hours to a datetime-local value ("YYYY-MM-DDTHH:mm")
 * and return another datetime-local value.
 */
export function addHoursToInputValue(inputValue, hours) {
  if (!inputValue) return "";
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(inputValue)) return inputValue;
  const [datePart, timePart] = inputValue.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);

  // Build a Date in local env, then just read back digits (no tz math intended)
  const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
  if (isNaN(dt.getTime())) return inputValue;
  dt.setHours(dt.getHours() + (hours || 0));
  const yyyy = dt.getFullYear();
  const MM = pad(dt.getMonth() + 1);
  const DD = pad(dt.getDate());
  const HH = pad(dt.getHours());
  const MI = pad(dt.getMinutes());
  return `${yyyy}-${MM}-${DD}T${HH}:${MI}`;
}

import {
  formatDateTime as formatRegionalDateTime,
  formatDateInput,
  toISODateTimeFromInput,
} from './regionalFormat';

/**
 * DISPLAY with time per regional settings
 * Accepts:
 *  - "YYYY-MM-DD"
 *  - "YYYY-MM-DDTHH:mm"
 *  - "YYYY-MM-DDTHH:mm:ss"
 *  - Date or epoch (number) as fallback
 */
export function formatWallTimeDisplay(value) {
  return formatRegionalDateTime(value);
}

/** @deprecated Use formatWallTimeDisplay */
export const formatISTDisplay = formatWallTimeDisplay;

/**
 * DISPLAY date-only using regional date pattern.
 */
export function formatDateOnly(value, sep) {
  if (!value) return "";
  const formatted = formatDateInput(value);
  if (!sep || sep === "/") return formatted;
  return formatted.replace(/\//g, sep);
}

/**
 * Format datetime-local value to dd/mm/yyyy HH:mm for display
 * Input: "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
 * Output: "dd/mm/yyyy HH:mm"
 */
export function formatInputValueForDisplay(inputValue) {
  return formatRegionalDateTime(inputValue);
}

/**
 * Parse regional datetime display to datetime-local value (YYYY-MM-DDTHH:mm).
 */
export function parseDisplayToInputValue(displayValue) {
  return toISODateTimeFromInput(displayValue);
}

